/**
 * Search scope types and serializers for the /api/search `scopes` query param.
 *
 * The backend accepts these token shapes:
 *   - 'own'                                              → caller's own items
 *   - 'own:public' | 'own:private'                       → narrowed by visibility
 *   - 'public'                                           → all public items
 *   - 'delegate'                                         → all caller-delegated collections (legacy)
 *   - 'delegate:<ownerId>:<collectionId>'                → one specific delegated collection
 *   - 'delegate:<ownerId>:<collectionId>:public|private' → narrowed by visibility
 *
 * The simple/structured forms cover the sidebar tree's parent rows; the
 * `:public` / `:private` suffix variants cover the expandable child rows.
 *
 * Canonical serialize order (URL-stability invariant; enforced by
 * `canonicalizeScopes`):
 *   1. own-family tokens (`own`, `own:public`, `own:private`) — bare first,
 *      then `:public`, then `:private`.
 *   2. `delegate:<ownerId>:<collectionId>[:visibility]` tokens, sorted
 *      lexicographically by `<ownerId>:<collectionId>`. Within a single
 *      delegated catalog: bare → `:public` → `:private`.
 *   3. `public` last.
 */

export type Visibility = 'public' | 'private';

export type SearchScope =
  | 'own'
  | 'public'
  | 'delegate'
  | { kind: 'own'; visibility: Visibility }
  | {
      kind: 'delegate';
      ownerId: string;
      collectionId: string;
      visibility?: Visibility;
    };

/**
 * Convert a single scope value to its wire token.
 */
export function serializeScope(scope: SearchScope): string {
  if (typeof scope === 'string') {
    return scope;
  }
  if (scope.kind === 'own') {
    return `own:${scope.visibility}`;
  }
  // Runtime guard: the wire format relies on splitting on ':'. If a future
  // ID format ever introduced colons we want a loud failure here rather
  // than a silently-corrupted auth token reaching the backend parser.
  if (scope.ownerId.includes(':') || scope.collectionId.includes(':')) {
    throw new Error(
      `serializeScope: a colon (':') is not allowed in ownerId or collectionId — ` +
        `the wire format 'delegate:<ownerId>:<collectionId>' is split on ':' ` +
        `by the backend. Got ownerId=${JSON.stringify(scope.ownerId)} ` +
        `collectionId=${JSON.stringify(scope.collectionId)}.`,
    );
  }
  const base = `delegate:${scope.ownerId}:${scope.collectionId}`;
  return scope.visibility ? `${base}:${scope.visibility}` : base;
}

export function serializeScopes(scopes: SearchScope[]): string {
  return scopes.map(serializeScope).join(',');
}

function isVisibility(v: string): v is Visibility {
  return v === 'public' || v === 'private';
}

/**
 * Inverse of {@link serializeScope}. Returns `null` for unrecognized tokens
 * so callers can safely drop garbage out of a malformed URL or stale
 * saved-search payload.
 */
export function parseScope(token: string): SearchScope | null {
  const trimmed = token.trim();
  if (trimmed === 'own' || trimmed === 'public' || trimmed === 'delegate') {
    return trimmed;
  }
  // own:public | own:private
  if (trimmed.startsWith('own:')) {
    const parts = trimmed.split(':');
    if (parts.length === 2 && isVisibility(parts[1])) {
      return { kind: 'own', visibility: parts[1] };
    }
    return null;
  }
  if (trimmed.startsWith('delegate:')) {
    const parts = trimmed.split(':');
    // delegate:<ownerId>:<collectionId>
    if (parts.length === 3 && parts[1] && parts[2]) {
      return { kind: 'delegate', ownerId: parts[1], collectionId: parts[2] };
    }
    // delegate:<ownerId>:<collectionId>:<visibility>
    if (parts.length === 4 && parts[1] && parts[2] && isVisibility(parts[3])) {
      return {
        kind: 'delegate',
        ownerId: parts[1],
        collectionId: parts[2],
        visibility: parts[3],
      };
    }
  }
  return null;
}

export function parseScopes(serialized: string | null | undefined): SearchScope[] {
  if (!serialized) return [];
  const out: SearchScope[] = [];
  for (const raw of serialized.split(',')) {
    const parsed = parseScope(raw);
    if (parsed !== null) {
      out.push(parsed);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// canonicalizeScopes
// ---------------------------------------------------------------------------

type ParentKey = 'own' | 'public' | 'delegate-bare' | `delegate:${string}:${string}`;

function parentKey(s: SearchScope): ParentKey {
  if (s === 'own' || s === 'public') return s;
  if (s === 'delegate') return 'delegate-bare';
  if (s.kind === 'own') return 'own';
  return `delegate:${s.ownerId}:${s.collectionId}`;
}

function visibilityOf(s: SearchScope): Visibility | null {
  return typeof s === 'string' ? null : (s.visibility ?? null);
}

function isBare(s: SearchScope): boolean {
  if (typeof s === 'string') return true;
  if (s.kind === 'own') return false; // narrowed own always has visibility
  return s.visibility === undefined;
}

/**
 * Sort key per parent group:
 *   own-family (0) → delegate:* (1, sorted by ownerId:collectionId) → public (2)
 * Within a group: bare (0) → :public (1) → :private (2).
 */
function sortKey(s: SearchScope): [number, string, number] {
  const getGroupKey = (): [number, string] => {
    if (s === 'own' || (typeof s !== 'string' && s.kind === 'own')) {
      return [0, ''];
    }
    if (s === 'delegate') {
      return [1, ''];
    }
    if (typeof s !== 'string' && s.kind === 'delegate') {
      return [1, `${s.ownerId}:${s.collectionId}`];
    }
    return [2, ''];
  };

  const [group, id] = getGroupKey();
  const visKey = isBare(s) ? 0 : (visibilityOf(s) === 'public' ? 1 : 2);
  return [group, id, visKey];
}

/**
 * Apply the parent/child selection state machine + sort:
 *   - if a bare parent is present, drop any narrowed-child siblings
 *   - if both narrowed-children are present, collapse to the bare parent
 *   - dedupe exact equals
 *   - return a deterministically-sorted list (canonical wire order)
 */
export function canonicalizeScopes(scopes: SearchScope[]): SearchScope[] {
  // Group by parent key.
  const groups = new Map<ParentKey, SearchScope[]>();
  for (const s of scopes) {
    const key = parentKey(s);
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(s);
  }

  const out: SearchScope[] = [];
  for (const [key, members] of groups) {
    // public, delegate-bare have no narrowed children to collapse.
    if (key === 'public' || key === 'delegate-bare') {
      out.push(members[0]);
      continue;
    }
    const bareMember = members.find(isBare);
    if (bareMember) {
      out.push(bareMember);
      continue;
    }
    const hasPublic = members.some((m) => visibilityOf(m) === 'public');
    const hasPrivate = members.some((m) => visibilityOf(m) === 'private');
    if (hasPublic && hasPrivate) {
      // Collapse both narrowed children into the bare parent.
      if (key === 'own') {
        out.push('own');
      } else {
        // delegate:<ownerId>:<collectionId>
        const sample = members[0] as Extract<SearchScope, { kind: 'delegate' }>;
        out.push({
          kind: 'delegate',
          ownerId: sample.ownerId,
          collectionId: sample.collectionId,
        });
      }
      continue;
    }
    // Single narrowed form (or duplicates of one). Take the first; dedupe.
    const seen = new Set<string>();
    for (const m of members) {
      const ser = serializeScope(m);
      if (!seen.has(ser)) {
        seen.add(ser);
        out.push(m);
      }
    }
  }

  out.sort((a, b) => {
    const ka = sortKey(a);
    const kb = sortKey(b);
    if (ka[0] !== kb[0]) return ka[0] - kb[0];
    if (ka[1] !== kb[1]) return ka[1] < kb[1] ? -1 : 1;
    return ka[2] - kb[2];
  });
  return out;
}
